import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { MapPin, Plus, Building2, Landmark, Trash2, ChevronDown, Edit2, Search } from 'lucide-react';
import { makeRESTApiRequest } from '../../helpers/api';
import { ENDPOINTS } from '../../helpers/constants';
import { toast } from 'sonner';
import AlertModal from '../AlertModal';
import CircularProgressBar from '../common/circular-progress';
import { groupByCounty } from '.';
import useMunicipalityCounty from '../../hooks/useMunicipalityCounty';
import LocationSubSection from './LocationSubSection';
import CountyFormModal from './CountyFormModal';

const CountyMunicipalityModal = ({ show, onHide, onDataChange }) => {
  const {
    records,
    loading,
    refreshRecords,
    handleAddMunicipalityRecord,
    handleDeleteCountyRecord,
    handleDeleteMunicipalityRecord,
    handleAddCityRecord,
    handleDeleteCityRecord,
    countyNames,
    municipalityNames,
    cityNames,
    mergeRecord,
    patchRecords
  } = useMunicipalityCounty();
  const [deletingRecord, setDeletingRecord] = useState(false);
  const [countyName, setCountyName] = useState('');
  const [expandedCounties, setExpandedCounties] = useState({});
  const [deleteMeta, setDeleteMeta] = useState(null);
  const [hoveredCounty, setHoveredCounty] = useState(null);
  // null = closed, { county: '' } = adding, { county: 'Name' } = editing that county
  const [countyFormMeta, setCountyFormMeta] = useState(null);
  const [defaultProcessorsByCounty, setDefaultProcessorsByCounty] = useState({});
  const [searchTerm, setSearchTerm] = useState('');

  const counties = groupByCounty(records);

  const filteredCounties = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return counties;

    return counties
      .map(countyEntry => {
        const countyMatches = countyEntry.county.toLowerCase().includes(query);
        if (countyMatches) return countyEntry;

        const matchingMunicipalities = countyEntry.municipalities.filter(m => m.city.toLowerCase().includes(query));
        const matchingCities = countyEntry.cities.filter(c => c.city.toLowerCase().includes(query));
        if (matchingMunicipalities.length > 0 || matchingCities.length > 0) {
          return { ...countyEntry, municipalities: matchingMunicipalities, cities: matchingCities };
        }
        return null;
      })
      .filter(Boolean);
  }, [counties, searchTerm]);

  const loadDefaultProcessors = useCallback(async () => {
    const { response, error } = await makeRESTApiRequest({
      endpoint: ENDPOINTS.COUNTY_DEFAULT_PROCESSORS,
      method: 'GET'
    });
    if (error) {
      toast.error(error);
      return;
    }
    setDefaultProcessorsByCounty(
      (Array.isArray(response) ? response : []).reduce((acc, item) => ({ ...acc, [item.county]: item.processors }), {})
    );
  }, []);

  useEffect(() => {
    if (!show) return;
    refreshRecords({ silent: true });
    loadDefaultProcessors();
  }, [show, refreshRecords, loadDefaultProcessors]);

  const handleAddCounty = () => {
    const county = countyName?.trim();
    if (!county) return;
    setCountyFormMeta({ county: '', initialName: county });
  };

  const handleCountySaved = ({ previousCounty, county, processors, countyMunicipality }) => {
    if (countyMunicipality) {
      mergeRecord(countyMunicipality);
      setCountyName('');
    } else {
      // Also drops the cached app choices, so the users' counties are fresh on the next load
      patchRecords(prev => prev.map(r => (r.county === previousCounty ? { ...r, county } : r)));
    }
    setDefaultProcessorsByCounty(prev => {
      const next = { ...prev };
      delete next[previousCounty];
      if (processors?.length) next[county] = processors;
      return next;
    });
    setCountyFormMeta(null);
    onDataChange?.();
  };

  const handleAddMunicipality = async (countyKey, value) => {
    const { error } = await handleAddMunicipalityRecord(countyKey, value);
    if (!error) onDataChange?.();
    return { error };
  };

  const handleAddCity = async (countyKey, value) => {
    const { error } = await handleAddCityRecord(countyKey, value);
    if (!error) onDataChange?.();
    return { error };
  };

  const handleDeleteCounty = async () => {
    if (!deleteMeta?.county) return;
    const countyToDelete = deleteMeta.county;
    setDeletingRecord(true);
    const { error } = await handleDeleteCountyRecord(countyToDelete);
    setDeletingRecord(false);
    setDeleteMeta(null);
    if (error) return;
    setDefaultProcessorsByCounty(prev => {
      const next = { ...prev };
      delete next[countyToDelete];
      return next;
    });
    onDataChange?.();
  };

  const handleDeleteMunicipality = async () => {
    if (!deleteMeta?.recordId) return;
    const recordId = deleteMeta.recordId;
    setDeletingRecord(true);
    const { error } = await handleDeleteMunicipalityRecord(recordId);
    setDeletingRecord(false);
    setDeleteMeta(null);
    if (!error) onDataChange?.();
  };

  const handleDeleteCity = async () => {
    if (!deleteMeta?.recordId) return;
    const recordId = deleteMeta.recordId;
    setDeletingRecord(true);
    const { error } = await handleDeleteCityRecord(recordId);
    setDeletingRecord(false);
    setDeleteMeta(null);
    if (!error) onDataChange?.();
  };

  const toggleCounty = county => {
    setExpandedCounties(prev => ({ ...prev, [county]: !prev[county] }));
  };

  const handleSaveMunicipalityEntry = async (id, county, newValue) => {
    const { error } = await makeRESTApiRequest({
      endpoint: ENDPOINTS.COUNTY_MUNICIPALITY_WITH_ID(id),
      method: 'PUT',
      requestBody: { county, city: newValue }
    });
    if (error) {
      toast.error(error || 'Failed to update municipality');
    } else {
      patchRecords(prev => prev.map(r => (r._id === id ? { ...r, city: newValue } : r)));
      onDataChange?.();
      toast.success('Municipality renamed successfully.');
    }
    return { error };
  };

  const handleSaveCityEntry = async (id, county, newValue) => {
    const { error } = await makeRESTApiRequest({
      endpoint: ENDPOINTS.COUNTY_MUNICIPALITY_WITH_ID(id),
      method: 'PUT',
      requestBody: { county, city: '', countyCity: newValue }
    });
    if (error) {
      toast.error(error || 'Failed to update city');
    } else {
      patchRecords(prev => prev.map(r => (r._id === id ? { ...r, countyCity: newValue } : r)));
      onDataChange?.();
      toast.success('City renamed successfully.');
    }
    return { error };
  };

  const handleClose = () => {
    setSearchTerm('');
    setCountyName('');
    setExpandedCounties({});
    setDeleteMeta(null);
    setCountyFormMeta(null);
    onHide();
  };

  return (
    <>
      <Modal
        show={show}
        onHide={handleClose}
        centered
        size="lg"
        dialogClassName="county-modal-dialog"
        contentClassName="scrollable-modal-content"
      >
        <Modal.Header closeButton>
          <div>
            <Modal.Title as="h2" className="mb-0 font-weight-bold xxlargeFont">
              <span>Counties, Cities & Municipalities</span>
              <p className="smallFont font-weight-normal text-muted mb-0">Manage your counties database</p>
            </Modal.Title>
          </div>
        </Modal.Header>

        <Modal.Body style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflowY: 'auto' }}>
          <div className="mb-3">
            <Form.Label className="small text-muted d-block mb-2">ADD NEW COUNTY</Form.Label>
            <div className="d-flex">
              <input
                type="text"
                className="county-input-custom mr-2"
                style={{ flex: 1 }}
                placeholder="Enter county name…"
                value={countyName}
                onChange={e => setCountyName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && countyName.trim() && handleAddCounty()}
                disabled={loading}
                list="county-datalist"
              />
              <datalist id="county-datalist">
                {countyNames.map(c => (
                  <option key={c} value={c} />
                ))}
              </datalist>
              <Button
                variant="success"
                onClick={handleAddCounty}
                disabled={loading || !countyName.trim()}
                className="d-flex align-items-center justify-content-center"
              >
                <Plus size={16} className="mr-1" />
                Add County
              </Button>
            </div>
          </div>

          <Form.Label className="small text-muted d-block mb-2">COUNTIES ({filteredCounties.length})</Form.Label>
          <div className="mb-3 position-relative">
            <input
              type="text"
              className="county-input-custom"
              style={{ paddingLeft: '32px' }}
              placeholder="Search counties, cities or municipalities..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              disabled={loading}
            />
            <Search
              size={16}
              className="position-absolute text-muted"
              style={{ left: '10px', top: '50%', transform: 'translateY(-50%)' }}
            />
          </div>

          {loading ? (
            <div
              className="text-center"
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                width: '100%'
              }}
            >
              <div className="d-inline-flex align-items-center justify-content-center mb-2">
                <CircularProgressBar size={2} />
              </div>
              <p className="largeFont font-weight-bold mb-0">Loading locations</p>
              <p className="smallFont text-muted mb-0">This will just take a moment...</p>
            </div>
          ) : filteredCounties.length === 0 ? (
            <div
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                textAlign: 'center',
                width: '100%'
              }}
            >
              <div
                className="d-inline-flex align-items-center justify-content-center w-80 h-80 rounded-circle mb-2 p-2"
                style={{ backgroundColor: '#e8f7f6' }}
              >
                <MapPin size={34} style={{ color: '#1d726a' }} />
              </div>
              <p className="largeFont font-weight-bold mb-1">
                {searchTerm.trim() ? 'No matches found' : 'No counties yet'}
              </p>
              <p className="smallFont text-muted mb-0">
                {searchTerm.trim()
                  ? 'Try a different county, municipality or city name'
                  : 'Get started by adding your first county above'}
              </p>
            </div>
          ) : (
            <div>
              {filteredCounties.map(c => (
                <div
                  key={c.county}
                  className="bg-white hover-border-dark mb-2 overflow-hidden"
                  style={{ border: '2px solid #e5e7ea', borderRadius: '10px', transition: 'all 0.2s ease' }}
                  onMouseEnter={() => setHoveredCounty(c.county)}
                  onMouseLeave={() => setHoveredCounty(null)}
                >
                  <div
                    className="d-flex align-items-center px-3 py-2 hover-light "
                    onClick={() => toggleCounty(c.county)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={e => e.key === 'Enter' && toggleCounty(c.county)}
                  >
                    <div
                      className="d-flex align-items-center justify-content-center p-2 rounded-circle flex-shrink-0 mr-3"
                      style={{ backgroundColor: '#e8f7f6' }}
                    >
                      <MapPin size={16} style={{ color: '#1d726a' }} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="smallFont font-weight-semibold mb-1">{c.county}</div>
                      <div className="tinyFont text-muted">
                        {c.cities.length} Cit{c.cities.length <= 1 ? 'y' : 'ies'} · {c.municipalities.length}{' '}
                        Municipalit
                        {c.municipalities.length <= 1 ? 'y' : 'ies'}
                        {defaultProcessorsByCounty[c.county]?.length > 0 &&
                          ` · ${defaultProcessorsByCounty[c.county].map(p => p.name || p.email).join(', ')}`}
                      </div>
                    </div>
                    <div
                      style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                      onClick={e => e.stopPropagation()}
                    >
                      <button
                        type="button"
                        className="county-edit-btn"
                        onClick={() => setCountyFormMeta({ county: c.county, initialName: c.county })}
                        style={{ opacity: hoveredCounty === c.county ? 1 : 0 }}
                        aria-label="Edit county"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        type="button"
                        className="county-delete-btn"
                        onClick={() => setDeleteMeta({ county: c.county })}
                        style={{ opacity: hoveredCounty === c.county ? 1 : 0 }}
                        aria-label="Delete county"
                      >
                        <Trash2 size={16} />
                      </button>
                      <div
                        style={{
                          color: '#64748b',
                          transition: 'transform 0.2s ease',
                          transform: expandedCounties[c.county] ? 'rotate(0deg)' : 'rotate(-90deg)',
                          cursor: 'pointer'
                        }}
                        onClick={() => toggleCounty(c.county)}
                      >
                        <ChevronDown size={20} />
                      </div>
                    </div>
                  </div>

                  {expandedCounties[c.county] && (
                    <div style={{ padding: '0 14px 14px 14px', borderTop: '1px solid #f1f5f9' }}>
                      <LocationSubSection
                        label="Cities"
                        items={c.cities}
                        county={c.county}
                        loading={loading}
                        icon={Landmark}
                        datalistOptions={cityNames}
                        onAdd={value => handleAddCity(c.county, value)}
                        onSave={(item, newValue) => handleSaveCityEntry(item.id, c.county, newValue)}
                        onDelete={(id, name) => setDeleteMeta({ recordId: id, cityName: name, type: 'city' })}
                      />
                      <LocationSubSection
                        label="Municipalities"
                        items={c.municipalities}
                        county={c.county}
                        loading={loading}
                        icon={Building2}
                        datalistOptions={municipalityNames}
                        onAdd={value => handleAddMunicipality(c.county, value)}
                        onSave={(item, newValue) => handleSaveMunicipalityEntry(item.id, c.county, newValue)}
                        onDelete={(id, name) => setDeleteMeta({ recordId: id, cityName: name, type: 'municipality' })}
                        className="mt-2"
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Modal.Body>
      </Modal>

      <CountyFormModal
        show={countyFormMeta !== null}
        county={countyFormMeta?.county}
        initialName={countyFormMeta?.initialName}
        initialProcessors={defaultProcessorsByCounty[countyFormMeta?.county] || []}
        onHide={() => setCountyFormMeta(null)}
        onSaved={handleCountySaved}
      />

      <AlertModal
        show={deleteMeta !== null}
        onHide={() => !deletingRecord && setDeleteMeta(null)}
        onDismissClick={() => !deletingRecord && setDeleteMeta(null)}
        onContinueClick={
          deleteMeta?.county
            ? handleDeleteCounty
            : deleteMeta?.type === 'city'
            ? handleDeleteCity
            : handleDeleteMunicipality
        }
        dismissButtonText="Cancel"
        continueButtonText="Delete"
        alertText={
          deleteMeta?.county
            ? `Delete "${deleteMeta.county}" and all its municipalities and cities? This action cannot be undone!`
            : deleteMeta?.cityName
            ? `Delete ${deleteMeta?.type === 'city' ? 'city' : 'municipality'} "${
                deleteMeta.cityName
              }"? This action cannot be undone!`
            : 'Delete this record? This action cannot be undone!'
        }
        showProgress={deletingRecord}
        progressText="Deleting..."
      />
    </>
  );
};

export default CountyMunicipalityModal;
